import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Config } from '../../types';
import { downloadDocument } from '../../services/api';
import { useAuthToken } from '../../contexts/AuthTokenContext';
import './PDFViewer.css';

interface Props {
  url: string;
  config: Config;
  onClose: () => void;
}

export const PDFViewer: React.FC<Props> = ({ url, config, onClose }) => {
  const { t } = useTranslation();
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getToken, account } = useAuthToken();
  const userId = account?.username || account?.localAccountId || 'anonymous';

  useEffect(() => {
    let currentPdfUrl: string | null = null;

    const loadPdf = async () => {
      if (url) {
        try {
          console.log('Loading PDF from URL:', url);
          const token = await getToken();
          const data = await downloadDocument(url, { token, userId, apiUrl: config.apiUrl });
          console.log('PDF data received:', data ? 'success' : 'null');
          const blob = new Blob([data], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          currentPdfUrl = blobUrl;
          setPdfData(blobUrl);
        } catch (err) {
          console.error('Error loading PDF:', err);
          setError(err instanceof Error ? err.message : 'Failed to load PDF');
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      if (currentPdfUrl) {
        URL.revokeObjectURL(currentPdfUrl);
      }
    };
  }, [url, getToken, userId, config.apiUrl]);

  if (error) {
    return (
      <div className="pdf-viewer-container">
        <div className="pdf-viewer-error">
          <p>{t('pdf.error', { error })}</p>
          <button onClick={onClose}>{t('pdf.backToChat')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-main">
        {isLoading ? (
          <div className="loading-spinner" />
        ) : pdfData ? (
          <embed
            src={pdfData}
            type="application/pdf"
            width="100%"
            height="100%"
            className="pdf-embed"
          />
        ) : (
          <div className="pdf-loading">{t('pdf.loading')}</div>
        )}
      </div>
    </div>
  );
}; 